"use client"

import type { HTMLStyledProps, ThemeProps } from "@/components/ui"
import type { ReactNode } from "react"
import type { FieldStyle } from "./field.style"
import { isValidElement, useId, useMemo, useState } from "react"
import {
  createContext,
  createSlotComponent,
  dataAttr,
  styled,
  useSplitChildren,
} from "@/components/ui"
import { useFieldsetContext } from "@/components/ui/components/fieldset"
import { fieldStyle } from "./field.style"

export interface FieldContext
  extends FieldProps,
    Pick<FieldRootProps, "replace"> {
  id: string
  errorMessageId: string
  focused: boolean
  helperMessageId: string
  labelId: string
  onBlur: () => void
  onFocus: () => void
}

const [FieldContext, useFieldContext] = createContext<FieldContext>({
  name: "FieldContext",
  strict: false,
})

export { FieldContext, useFieldContext }

export interface FieldProps {
  /**
   * If `true`, the field will be disabled.
   *
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the field will be invalid.
   *
   * @default false
   */
  invalid?: boolean
  /**
   * If `true`, the field will be readonly.
   *
   * @default false
   */
  readOnly?: boolean
  /**
   * If `true`, the field will be required.
   *
   * @default false
   */
  required?: boolean
}

export interface FieldRootProps
  extends HTMLStyledProps,
    ThemeProps<FieldStyle>,
    Pick<FieldLabelProps, "optionalIndicator" | "requiredIndicator">,
    FieldProps {
  /**
   * The field error message to use.
   */
  errorMessage?: ReactNode
  /**
   * The field helper message to use.
   */
  helperMessage?: ReactNode
  /**
   * The field label to use.
   */
  label?: ReactNode
  /**
   * If `true`, switch between helper message and error message using invalid.
   *
   * @default true
   */
  replace?: boolean
  /**
   * Props the error message component.
   */
  errorMessageProps?: FieldErrorMessageProps
  /**
   * Props the helper message component.
   */
  helperMessageProps?: FieldHelperMessageProps
  /**
   * Props the label component.
   */
  labelProps?: FieldLabelProps
}

const {
  PropsContext: FieldPropsContext,
  usePropsContext: useFieldPropsContext,
  withContext,
  withProvider,
} = createSlotComponent<FieldRootProps, FieldStyle>("field", fieldStyle)

export { FieldPropsContext, useFieldPropsContext }

/**
 * `Field` is a component used to group form elements with label, helper message, error message, etc.
 *
 * @see https://yamada-ui.com/docs/components/field
 */
export const FieldRoot = withProvider<"div", FieldRootProps>(
  ({
    id,
    children,
    disabled,
    errorMessage,
    helperMessage,
    invalid,
    label,
    optionalIndicator,
    readOnly,
    replace = true,
    required,
    requiredIndicator,
    errorMessageProps,
    helperMessageProps,
    labelProps,
    ...rest
  }) => {
    const uuid = useId()
    const labelId = useId()
    const helperMessageId = useId()
    const errorMessageId = useId()
    const [focused, setFocused] = useState<boolean>(false)
    const [
      omittedChildren,
      customLabel,
      customHelperMessage,
      customErrorMessage,
    ] = useSplitChildren(
      children,
      FieldLabel,
      FieldHelperMessage,
      FieldErrorMessage,
    )

    id ??= uuid

    const context = useMemo(
      () => ({
        id,
        disabled,
        errorMessageId,
        focused,
        helperMessageId,
        invalid,
        labelId,
        readOnly,
        replace,
        required,
        onBlur: () => setFocused(false),
        onFocus: () => setFocused(true),
      }),
      [
        id,
        disabled,
        labelId,
        focused,
        invalid,
        helperMessageId,
        errorMessageId,
        readOnly,
        replace,
        required,
      ],
    )

    return (
      <FieldContext value={context}>
        <styled.div
          data-disabled={dataAttr(disabled)}
          data-focus={dataAttr(focused)}
          data-invalid={dataAttr(invalid)}
          data-readonly={dataAttr(readOnly)}
          {...rest}
        >
          {customLabel ||
            (label ? (
              <FieldLabel
                optionalIndicator={optionalIndicator}
                requiredIndicator={requiredIndicator}
                {...labelProps}
              >
                {label}
              </FieldLabel>
            ) : null)}

          {omittedChildren}

          {customHelperMessage ||
            (helperMessage ? (
              <FieldHelperMessage {...helperMessageProps}>
                {helperMessage}
              </FieldHelperMessage>
            ) : null)}

          {customErrorMessage ||
            (errorMessage ? (
              <FieldErrorMessage {...errorMessageProps}>
                {errorMessage}
              </FieldErrorMessage>
            ) : null)}
        </styled.div>
      </FieldContext>
    )
  },
  "root",
)()

export interface FieldLabelProps extends HTMLStyledProps<"label"> {
  /**
   * The optional indicator to use.
   */
  optionalIndicator?: ReactNode
  /**
   * If `true`, the field will be required.
   *
   * @default false
   */
  required?: boolean
  /**
   * The required indicator to use.
   */
  requiredIndicator?: ReactNode
}

export const FieldLabel = withContext<"label", FieldLabelProps>(
  ({
    id,
    htmlFor,
    children,
    optionalIndicator = null,
    required,
    requiredIndicator = null,
    ...rest
  }) => {
    const fieldsetContext = useFieldsetContext()
    const fieldContext = useFieldContext()

    id ??= fieldContext?.labelId
    htmlFor ??= fieldContext?.id
    required ??= fieldContext?.required ?? fieldsetContext?.required

    const disabled = fieldContext?.disabled ?? fieldsetContext?.disabled
    const invalid = fieldContext?.invalid ?? fieldsetContext?.invalid
    const readOnly = fieldContext?.readOnly ?? fieldsetContext?.readOnly
    const focused = fieldContext?.focused

    return (
      <styled.label
        id={id}
        htmlFor={htmlFor}
        data-disabled={dataAttr(disabled)}
        data-focus={dataAttr(focused)}
        data-invalid={dataAttr(invalid)}
        data-readonly={dataAttr(readOnly)}
        {...rest}
      >
        {children}

        {required ? (
          requiredIndicator ? (
            <FieldRequiredIndicator>{requiredIndicator}</FieldRequiredIndicator>
          ) : (
            <FieldRequiredIndicator />
          )
        ) : (
          optionalIndicator
        )}
      </styled.label>
    )
  },
  "label",
)()

interface FieldRequiredIndicatorProps extends HTMLStyledProps<"span"> {}

const FieldRequiredIndicator = withContext<"span", FieldRequiredIndicatorProps>(
  ({ children, ...rest }) => {
    if (!isValidElement(children)) {
      return (
        <styled.span aria-hidden role="presentation" {...rest}>
          {children ?? <>*</>}
        </styled.span>
      )
    } else {
      return children
    }
  },
  "requiredIndicator",
)()

export interface FieldHelperMessageProps extends HTMLStyledProps<"span"> {}

export const FieldHelperMessage = withContext<"span", FieldHelperMessageProps>(
  (props) => {
    const fieldsetContext = useFieldsetContext()
    const {
      helperMessageId,
      invalid = fieldsetContext?.invalid,
      replace,
    } = useFieldContext() ?? {}

    return (
      <styled.span
        id={helperMessageId}
        hidden={replace && invalid ? true : false}
        {...props}
      />
    )
  },
  "helperMessage",
)()

export interface FieldErrorMessageProps extends HTMLStyledProps<"span"> {}

export const FieldErrorMessage = withContext<"span", FieldErrorMessageProps>(
  (props) => {
    const fieldsetContext = useFieldsetContext()
    const { errorMessageId, invalid = fieldsetContext?.invalid } =
      useFieldContext() ?? {}

    return (
      <styled.span
        id={errorMessageId}
        aria-live={invalid ? "polite" : undefined}
        hidden={!invalid}
        {...props}
      />
    )
  },
  "errorMessage",
)()
